import { z } from "zod";

/**
 * VibeApp.resourceQuotasJson — typed JSON column carrying the per-app
 * resource caps written into the app's Nomad jobspecs. `memoryMiB` is the most
 * the app may use: the HARD limit on its container, whatever its tenant's
 * resource shape reserves for it (`vibeTaskResources`). Distinct from the
 * per-org `VibeOrgCostSafetyState` gate (which can refuse a trigger entirely).
 *
 * 🔴 THEY BOUND THE APP, NEVER ITS BUILD. The deploy jobspec puts them on the
 * app's own container. The build jobspec carries the same numbers on the full
 * resource shape and a fixed envelope on the halved one, but its task is only
 * the build runner and the docker CLI: `docker build` executes every RUN step
 * inside the host's Docker daemon, outside that task's cgroup, so no value here
 * changes the memory or CPU a build step gets. A build that runs Node out of
 * heap is reported as `BUILD_HEAP_OUT_OF_MEMORY`, and one the system kills for
 * memory as `BUILD_STEP_OOM_KILLED`; raising `memoryMiB` does nothing for
 * either. Its levers are the build's own NODE_OPTIONS and the build's
 * memory budget, which sizes the heap default the runner applies
 * (`nodeHeapDefaultMiB`, @nexus/vibe-stack-detect) — the build envelope on the
 * halved shape (`VIBE_BUILD_ENVELOPE`), the build host's memory otherwise.
 *
 * The bounds below are what a tenant's app node can hold, and they are enforced
 * where a quota is WRITTEN. A row is READ through `clampVibeAppResourceQuotas`,
 * which brings a stored value outside them to the nearest bound instead of
 * refusing it: a stored quota is a decision somebody made, and replacing it with
 * the defaults would turn an app that was given 16 GiB into a 512 MiB app on its
 * next deploy.
 */

export const VIBE_APP_RESOURCE_QUOTAS_BOUNDS = {
  /// 100 MHz to 4,000 MHz per instance. With a tenant on the halved resource
  /// shape an app reserves a quarter of this, so the most any one app places is
  /// 1,000 MHz — the CPU of the build-sized hole the app pool keeps free.
  cpuMhz: { min: 100, max: 4_000 },
  /// 128 MiB to 6 GiB. Below 128 MiB most Node/Python runtimes can't even
  /// boot. 6,144 keeps a halved reservation (3,072) under the 4,096 MiB hole
  /// the app pool keeps free, and the limit itself under the 6,732 MiB a
  /// t3.large client offers apps once the OS reservation and the per-node
  /// platform jobs are taken (`appPlaceableNodeCapacity`).
  memoryMiB: { min: 128, max: 6_144 },
  /// 1 to 20 instances. The cap is the locked v1 "no per-tenant fleet
  /// expansion" floor — 20 covers blue-green during a rolling deploy
  /// (max-instances per color) without inviting unbounded scale-out.
  maxInstances: { min: 1, max: 20 }
} as const;

export const VibeAppResourceQuotasSchema = z.object({
  cpuMhz: z
    .number()
    .int()
    .min(VIBE_APP_RESOURCE_QUOTAS_BOUNDS.cpuMhz.min)
    .max(VIBE_APP_RESOURCE_QUOTAS_BOUNDS.cpuMhz.max),
  memoryMiB: z
    .number()
    .int()
    .min(VIBE_APP_RESOURCE_QUOTAS_BOUNDS.memoryMiB.min)
    .max(VIBE_APP_RESOURCE_QUOTAS_BOUNDS.memoryMiB.max),
  maxInstances: z
    .number()
    .int()
    .min(VIBE_APP_RESOURCE_QUOTAS_BOUNDS.maxInstances.min)
    .max(VIBE_APP_RESOURCE_QUOTAS_BOUNDS.maxInstances.max)
});

export type VibeAppResourceQuotas = z.infer<typeof VibeAppResourceQuotasSchema>;

/**
 * The shape a STORED quota must have to be read at all: three integers, with no
 * bounds. Bounds are a property of the write; a row that predates the current
 * ones is still a well-formed row, and is clamped rather than discarded.
 */
const VibeAppStoredResourceQuotasSchema = z.object({
  cpuMhz: z.number().int(),
  memoryMiB: z.number().int(),
  maxInstances: z.number().int()
});

/** What reading a stored quota produced. */
export type VibeAppStoredResourceQuotasRead =
  /** The stored value, brought inside the bounds. `clamped` names every field that moved. */
  | {
      kind: "read";
      quotas: VibeAppResourceQuotas;
      clamped: readonly (keyof VibeAppResourceQuotas)[];
    }
  /** Not three integers at all — there is no stored decision to keep. */
  | { kind: "unreadable"; reason: string };

const clampTo = (value: number, bound: { min: number; max: number }): number =>
  Math.min(bound.max, Math.max(bound.min, value));

/**
 * Read a stored `resourceQuotasJson` value. A well-formed value outside
 * `VIBE_APP_RESOURCE_QUOTAS_BOUNDS` is CLAMPED to the nearest bound and reported,
 * never replaced by `VIBE_APP_RESOURCE_QUOTAS_DEFAULTS` — only a value that is
 * not three integers is `unreadable`, and only that is the caller's to default.
 */
export function clampVibeAppResourceQuotas(stored: unknown): VibeAppStoredResourceQuotasRead {
  const parsed = VibeAppStoredResourceQuotasSchema.safeParse(stored);
  if (!parsed.success) return { kind: "unreadable", reason: parsed.error.message };
  const quotas: VibeAppResourceQuotas = {
    cpuMhz: clampTo(parsed.data.cpuMhz, VIBE_APP_RESOURCE_QUOTAS_BOUNDS.cpuMhz),
    memoryMiB: clampTo(parsed.data.memoryMiB, VIBE_APP_RESOURCE_QUOTAS_BOUNDS.memoryMiB),
    maxInstances: clampTo(parsed.data.maxInstances, VIBE_APP_RESOURCE_QUOTAS_BOUNDS.maxInstances)
  };
  const fields = ["cpuMhz", "memoryMiB", "maxInstances"] as const;
  return {
    kind: "read",
    quotas,
    clamped: fields.filter((field) => quotas[field] !== parsed.data[field])
  };
}

/**
 * Defaults applied at `VibeApp.create()` and as the fallback for
 * legacy rows whose `resourceQuotasJson` doesn't parse. The DB-level
 * `@default` on the column ensures backfill on migration; this
 * constant is the source of truth for the in-process default and
 * MUST stay in sync with the schema.prisma `@default(...)` literal.
 *
 * Sizing rationale: matches Railway / Fly.io "starter" defaults so a
 * fresh app boots a Node/Python service without operator tuning.
 * Operators raise these per-app via the admin PATCH endpoint when the
 * RUNNING app is OOM-killed or CPU-throttled — never for a failing build,
 * which these do not bound (see above).
 */
export const VIBE_APP_RESOURCE_QUOTAS_DEFAULTS: VibeAppResourceQuotas = {
  cpuMhz: 500,
  memoryMiB: 512,
  maxInstances: 3
};
